import { atom } from 'jotai';
import { getGroupReplies } from '@/apis/quick-reply';
import {
  GroupRepliesResponse,
  QuickReplyGroupItem,
  QuickReplyItem,
} from '@/interfaces/quick-reply';

// 基础数据原子
export const quickReplyDataAtom = atom<GroupRepliesResponse | null>(null);
export const quickReplyLoadingAtom = atom<boolean>(false);
export const quickReplyErrorAtom = atom<Error | null>(null);

// 默认数据
const DEFAULT_DATA: QuickReplyGroupItem[] = [];

// 计算原子 - 企业回复数据
export const enterpriseRepliesAtom = atom<QuickReplyGroupItem[]>((get) => {
  const data = get(quickReplyDataAtom);
  return data?.entGroups || DEFAULT_DATA;
});

// 计算原子 - 个人回复数据
export const personalRepliesAtom = atom<QuickReplyGroupItem[]>((get) => {
  const data = get(quickReplyDataAtom);
  return data?.agentGroups || DEFAULT_DATA;
});

// 计算原子 - 所有回复数据
export const allRepliesAtom = atom<{
  enterprise: QuickReplyGroupItem[];
  personal: QuickReplyGroupItem[];
}>((get) => ({
  enterprise: get(enterpriseRepliesAtom),
  personal: get(personalRepliesAtom),
}));

export const quickReplyAtom = atom(
  (get) => ({
    data: get(quickReplyDataAtom),
    loading: get(quickReplyLoadingAtom),
    error: get(quickReplyErrorAtom),
    enterprise: get(enterpriseRepliesAtom),
    personal: get(personalRepliesAtom),
    allData: get(allRepliesAtom),
  }),
  async (get, set) => {
    set(quickReplyLoadingAtom, true);
    set(quickReplyErrorAtom, null);

    try {
      const data = await getGroupReplies();
      set(quickReplyDataAtom, data);
    } catch (error) {
      set(quickReplyErrorAtom, error as Error);
    } finally {
      set(quickReplyLoadingAtom, false);
    }
  },
);

// 计算原子 - 所有快捷回复项
export const allQuickReplyItemsAtom = atom<QuickReplyItem[]>((get) => {
  const allData = get(allRepliesAtom);
  const allReplies: QuickReplyItem[] = [];

  // 个人回复在前
  allData.personal.forEach((group) => {
    if (group.replies) {
      allReplies.push(...group.replies);
    }
  });

  // 企业回复在后
  allData.enterprise.forEach((group) => {
    if (group.replies) {
      allReplies.push(...group.replies);
    }
  });

  return allReplies;
});

// 刷新数据的原子
export const refreshQuickReplyAtom = atom(null, async (get, set) => {
  await set(quickReplyAtom);
});

// 乐观更新原子 - 添加新分组
export const addGroupOptimisticallyAtom = atom(null, (get, set, newGroup: QuickReplyGroupItem) => {
  const currentData = get(quickReplyDataAtom);
  if (!currentData) return;

  const updatedData = { ...currentData };

  if (newGroup.ownerType === 'enterprise') {
    updatedData.entGroups = [...(updatedData.entGroups || []), newGroup];
  } else if (newGroup.ownerType === 'agent') {
    updatedData.agentGroups = [...(updatedData.agentGroups || []), newGroup];
  }

  set(quickReplyDataAtom, updatedData);
});

// 乐观更新原子 - 更新分组
export const updateGroupOptimisticallyAtom = atom(
  null,
  (get, set, groupId: number, updates: Partial<QuickReplyGroupItem>) => {
    const currentData = get(quickReplyDataAtom);
    if (!currentData) return;

    const updatedData = { ...currentData };

    // 更新企业分组
    if (updatedData.entGroups) {
      updatedData.entGroups = updatedData.entGroups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group,
      );
    }

    // 更新个人分组
    if (updatedData.agentGroups) {
      updatedData.agentGroups = updatedData.agentGroups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group,
      );
    }

    set(quickReplyDataAtom, updatedData);
  },
);

// 乐观更新原子 - 删除分组
export const deleteGroupOptimisticallyAtom = atom(null, (get, set, groupId: number) => {
  const currentData = get(quickReplyDataAtom);
  if (!currentData) return;

  const updatedData = { ...currentData };

  // 删除企业分组
  if (updatedData.entGroups) {
    updatedData.entGroups = updatedData.entGroups.filter((group) => group.id !== groupId);
  }

  // 删除个人分组
  if (updatedData.agentGroups) {
    updatedData.agentGroups = updatedData.agentGroups.filter((group) => group.id !== groupId);
  }

  set(quickReplyDataAtom, updatedData);
});
